<template>
    <div>
        <Loading :active="loading" :is-full-page="true" />
        <div align="center" justify="center" class="pa-3">
            <v-card align="left" justify="left" class="mb-3">
                <v-card-text class="pa-3 font-weight-light white black--text">
                    <v-icon class="primary--text lighten-2 pa-2">
                        mdi-alert-circle-outline
                    </v-icon>
                    Administração > Falha de Retornos
                </v-card-text>
            </v-card>

            <v-card @keyup.native.enter="aplicarFiltros" class="pa-3 mb-4">
                <v-row class="pa-3 d-flex align-center">
                    <v-col
                        cols="6"
                        xs="8"
                        sm="8"
                        md="6"
                        lg="6"
                        class="d-flex flex-row pa-0"
                    >
                        <v-autocomplete
                            v-model="filtroApresentante"
                            :items="apresentantesOptions"
                            item-text="name"
                            item-value="value"
                            label="Filtrar por Apresentante"
                            multiple
                            chips
                            deletable-chips
                            clearable
                            outlined
                            dense
                            hide-details
                            :disabled="loading"
                            data-cy="filtroApresentante"
                        >
                            <template v-slot:selection="{ item, index }">
                                <v-chip
                                    v-if="index === 0"
                                    small
                                    color="primary"
                                    text-color="white"
                                >
                                    {{ item.name }}
                                    <span
                                        v-if="filtroApresentante.length > 1"
                                        class="ml-1"
                                    >
                                        (+{{ filtroApresentante.length - 1 }})
                                    </span>
                                </v-chip>
                            </template>
                        </v-autocomplete>
                    </v-col>

                    <v-col class="d-flex justify-end align-center pa-0">
                        <v-btn
                            color="primary"
                            class="mr-2"
                            :disabled="loading"
                            @click="aplicarFiltros"
                            data-cy="btnFiltrar"
                        >
                            <v-icon left>mdi-magnify</v-icon>
                            Filtrar
                        </v-btn>
                        <v-btn
                            outlined
                            color="secondary"
                            :disabled="loading"
                            @click="limparFiltros"
                            data-cy="btnLimpar"
                        >
                            <v-icon left>mdi-filter-remove</v-icon>
                            Limpar
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card>

            <v-card class="pa-3">
                <v-card-actions class="pb-0">
                    <v-btn
                        color="warning"
                        :disabled="itensSelecionados.length === 0 || loading"
                        @click="abrirConfirmacaoIgnorarLote"
                        data-cy="btnIgnorarLote"
                    >
                        <v-icon left>mdi-eye-off</v-icon>
                        Ignorar Retornos ({{ itensSelecionados.length }})
                    </v-btn>
                </v-card-actions>
                
                <v-data-table
                    v-model="itensSelecionados"
                    :headers="headers"
                    :items="falhasRetorno"
                    :loading="loading"
                    :footer-props="{
                        itemsPerPageOptions: [10, 20, 50, 100],
                        itemsPerPageText: 'Itens por página:'
                    }"
                    item-key="id"
                    class="elevation-1"
                    :server-items-length="totalItems"
                    :page.sync="currentPage"
                    :items-per-page.sync="itemsPerPage"
                    @update:page="carregarDados"
                    @update:items-per-page="carregarDados"
                    show-select
                >
                    <template v-slot:[`item.dt_movimento`]="{ item }">
                        {{ formatarData(item.dt_movimento) }}
                    </template>

                    <template v-slot:[`item.dt_apontamento`]="{ item }">
                        {{ formatarData(item.dt_apontamento) }}
                    </template>

                    <template v-slot:[`item.titulo_dt_protocolo`]="{ item }">
                        {{ formatarData(item.titulo_dt_protocolo) }}
                    </template>

                    <template v-slot:[`item.acoes`]="{ item }">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    v-bind="attrs"
                                    v-on="on"
                                    icon
                                    small
                                    color="info"
                                    @click="abrirModalCorrecao(item)"
                                    data-cy="btnCorrecao"
                                >
                                    <v-icon>mdi-file-edit</v-icon>
                                </v-btn>
                            </template>
                            <span>Correção</span>
                        </v-tooltip>
                    </template>
                </v-data-table>
            </v-card>
        </div>

        <ModalEdicaoFalhaRetorno
            v-if="modalEdicaoAtivo"
            :showModal="modalEdicaoAtivo"
            :falhaRetorno="falhaRetornoSelecionada"
            :aguardandoReprocessamento="aguardandoReprocessamento"
            @fecharModal="fecharModalEdicao"
            @mostrarMensagem="mostrarMensagem"
            @atualizarLista="carregarDados"
        />

        <v-snackbar
            v-model="snackbar"
            :color="snackbarColor"
            timeout="5000"
            top
            right
        >
            {{ snackbarMessage }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar = false">
                    Fechar
                </v-btn>
            </template>
        </v-snackbar>

        <v-dialog v-model="dialogConfirmacao" max-width="400">
            <v-card>
                <v-card-title class="headline">
                    {{ confirmacao.titulo }}
                </v-card-title>
                <v-card-text>
                    {{ confirmacao.texto }}
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="green darken-1" text @click="confirmarAcao">
                        Confirmar
                    </v-btn>
                    <v-btn
                        color="red darken-1"
                        text
                        @click="cancelarConfirmacao"
                    >
                        Cancelar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dialogConfirmacaoLote" max-width="500">
            <v-card>
                <v-card-title class="headline warning--text">
                    <v-icon left color="warning">mdi-alert</v-icon>
                    Ignorar Retornos em Lote
                </v-card-title>
                <v-card-text class="pt-4">
                    <p class="text-body-1">
                        Você está prestes a ignorar
                        <strong>{{ itensSelecionados.length }}</strong>
                        {{ itensSelecionados.length === 1 ? 'retorno' : 'retornos' }}.
                    </p>
                    <p class="text-body-2 mb-0">
                        Esta ação não poderá ser desfeita. Deseja continuar?
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="grey darken-1"
                        text
                        @click="cancelarConfirmacaoLote"
                        :disabled="loadingIgnorarLote"
                    >
                        Cancelar
                    </v-btn>
                    <v-btn
                        color="warning"
                        @click="confirmarIgnorarLote"
                        :loading="loadingIgnorarLote"
                        :disabled="loadingIgnorarLote"
                    >
                        Confirmar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import FalhaRetornosService from '@/services/FalhaRetornosService';
import ModalEdicaoFalhaRetorno from '@/modals/ModalEdicaoFalhaRetorno.vue';
import Loading from 'vue-loading-overlay';
import moment from 'moment';

export default {
    name: 'FalhaRetornos',
    components: {
        ModalEdicaoFalhaRetorno,
        Loading
    },
    data() {
        return {
            loading: false,
            falhasRetorno: [],
            apresentantesOptions: [],
            filtroApresentante: [],
            modalEdicaoAtivo: false,
            falhaRetornoSelecionada: null,
            aguardandoReprocessamento: false,
            snackbar: false,
            snackbarMessage: '',
            snackbarColor: 'success',
            dialogConfirmacao: false,
            confirmacao: {
                titulo: '',
                texto: '',
                acao: null,
                item: null
            },
            itensSelecionados: [],
            dialogConfirmacaoLote: false,
            loadingIgnorarLote: false,
            currentPage: 1,
            itemsPerPage: 20,
            totalItems: 0,
            headers: [
                { text: 'ID', value: 'id', sortable: true },
                {
                    text: 'Código do Apresentante',
                    value: 'cd_apresentante',
                    sortable: true
                },
                { text: 'ID Título', value: 'id_titulo', sortable: true },
                { text: 'Nosso Número', value: 'nosso_numero', sortable: true },
                {
                    text: 'Data do Movimento',
                    value: 'dt_movimento',
                    sortable: true
                },
                {
                    text: 'Pendência',
                    value: 'problema_pendente',
                    sortable: false
                },
                {
                    text: 'Info Adicional',
                    value: 'info_adicional',
                    sortable: false
                },
                {
                    text: 'Protocolo Título',
                    value: 'protocolo_titulo',
                    sortable: false
                },
                {
                    text: 'Protocolo Recebido',
                    value: 'protocolo_recebido',
                    sortable: false
                },
                {
                    text: 'Cartório Título',
                    value: 'cartorio_titulo',
                    sortable: false
                },
                {
                    text: 'Cartório Recebido',
                    value: 'cartorio_recebido',
                    sortable: false
                },
                {
                    text: 'Código da Comarca',
                    value: 'cd_comarca',
                    sortable: false
                },
                {
                    text: 'Código do Estado do Devedor',
                    value: 'cd_estado_devedor',
                    sortable: false
                },
                {
                    text: 'Data do Apontamento',
                    value: 'dt_apontamento',
                    sortable: true
                },
                {
                    text: 'Data do Protocolo do Título',
                    value: 'titulo_dt_protocolo',
                    sortable: true
                },
                {
                    text: 'Código do Status',
                    value: 'cd_status_retorno',
                    sortable: false
                },
                {
                    text: 'Tipo de Ocorrência do Cartório',
                    value: 'tp_ocorrencia_cartorio',
                    sortable: false
                },
                {
                    text: 'Ações',
                    value: 'acoes',
                    sortable: false,
                    width: '200px'
                }
            ]
        };
    },
    async mounted() {
        await this.inicializar();
    },
    methods: {
        async inicializar() {
            this.loading = true;
            try {
                await Promise.all([
                    this.carregarApresentantes(),
                    this.carregarDados()
                ]);
            } catch (error) {
                this.mostrarMensagem(
                    'Erro ao carregar dados iniciais',
                    'error'
                );
                console.error('Erro na inicialização:', error);
            } finally {
                this.loading = false;
            }
        },

        async carregarApresentantes() {
            try {
                const service = new FalhaRetornosService(
                    this.$http,
                    this.$store
                );
                const response = await service.listarApresentantes();

                this.apresentantesOptions = [
                    { name: 'Todos', value: 'todos' },
                    ...response.data.map(apresentante => ({
                        name: `${apresentante.cd_apresentante} - ${apresentante.nm_apresentante}`,
                        value: apresentante.cd_apresentante
                    }))
                ];
            } catch (error) {
                console.error('Erro ao carregar apresentantes:', error);
            }
        },

        async carregarDados() {
            this.loading = true;
            try {
                const service = new FalhaRetornosService(
                    this.$http,
                    this.$store
                );
                const params = {
                    page: this.currentPage,
                    limit: this.itemsPerPage
                };

                if (this.filtroApresentante.length > 0) {
                    params.apresentantes = this.filtroApresentante.join(',');
                }

                const response = await service.listarFalhasRetorno(params);
                this.falhasRetorno = response.results;
                this.totalItems = response.total_results;
                
                this.itensSelecionados = [];
            } catch (error) {
                this.mostrarMensagem('Erro ao carregar dados', 'error');
                console.error('Erro ao carregar dados:', error);
            } finally {
                this.loading = false;
            }
        },

        aplicarFiltros() {
            this.currentPage = 1;
            this.carregarDados();
        },

        limparFiltros() {
            this.filtroApresentante = [];
            this.aplicarFiltros();
        },

        abrirModalEdicao(item) {
            this.falhaRetornoSelecionada = { ...item };
            this.modalEdicaoAtivo = true;
        },

        abrirModalParaReprocessar(item) {
            this.falhaRetornoSelecionada = { ...item };
            this.modalEdicaoAtivo = true;
            this.aguardandoReprocessamento = true;
        },

        abrirModalCorrecao(item) {
            this.falhaRetornoSelecionada = { ...item };
            this.modalEdicaoAtivo = true;
            this.aguardandoReprocessamento = false;
        },

        fecharModalEdicao() {
            this.modalEdicaoAtivo = false;
            this.falhaRetornoSelecionada = null;
            this.aguardandoReprocessamento = false;
        },

        reprocessarRetorno(item) {
            this.confirmacao = {
                titulo: 'Reprocessar Retorno',
                texto: `Deseja realmente reprocessar o retorno do título ${item.nosso_numero}?`,
                acao: 'reprocessar',
                item: item
            };
            this.dialogConfirmacao = true;
        },

        ignorarRetorno(item) {
            this.confirmacao = {
                titulo: 'Ignorar Retorno',
                texto: `Deseja realmente ignorar o retorno do título ${item.nosso_numero}?`,
                acao: 'ignorar',
                item: item
            };
            this.dialogConfirmacao = true;
        },

        async confirmarAcao() {
            this.dialogConfirmacao = false;
            this.loading = true;

            try {
                const service = new FalhaRetornosService(
                    this.$http,
                    this.$store
                );

                if (this.confirmacao.acao === 'reprocessar') {
                    await service.reprocessarRetorno(this.confirmacao.item.id);
                    this.mostrarMensagem(
                        'Retorno reprocessado com sucesso',
                        'success'
                    );
                } else if (this.confirmacao.acao === 'ignorar') {
                    await service.ignorarRetorno(this.confirmacao.item.id);
                    this.mostrarMensagem(
                        'Retorno ignorado com sucesso',
                        'success'
                    );
                }

                await this.carregarDados();
            } catch (error) {
                this.mostrarMensagem(
                    `Erro ao ${this.confirmacao.acao} retorno`,
                    'error'
                );
                console.error(`Erro ao ${this.confirmacao.acao}:`, error);
            } finally {
                this.loading = false;
            }
        },

        cancelarConfirmacao() {
            this.dialogConfirmacao = false;
            this.confirmacao = {
                titulo: '',
                texto: '',
                acao: null,
                item: null
            };
        },

        mostrarMensagem(mensagem, tipo = 'success') {
            this.snackbarMessage = mensagem;
            this.snackbarColor = tipo;
            this.snackbar = true;
        },

        formatarData(data) {
            return moment(data).format('DD/MM/YYYY');
        },

        abrirConfirmacaoIgnorarLote() {
            this.dialogConfirmacaoLote = true;
        },

        cancelarConfirmacaoLote() {
            this.dialogConfirmacaoLote = false;
        },

        async confirmarIgnorarLote() {
            this.loadingIgnorarLote = true;
            let sucessos = 0;
            let erros = 0;

            try {
                const service = new FalhaRetornosService(
                    this.$http,
                    this.$store
                );

                for (const item of this.itensSelecionados) {
                    try {
                        await service.ignorarRetorno(item.id);
                        sucessos++;
                    } catch (error) {
                        console.error(
                            `Erro ao ignorar retorno ${item.id}:`,
                            error
                        );
                        erros++;
                    }
                }

                if (erros === 0) {
                    this.mostrarMensagem(
                        `${sucessos} ${sucessos === 1 ? 'retorno ignorado' : 'retornos ignorados'} com sucesso`,
                        'success'
                    );
                } else if (sucessos === 0) {
                    this.mostrarMensagem(
                        'Erro ao ignorar todos os retornos',
                        'error'
                    );
                } else {
                    this.mostrarMensagem(
                        `${sucessos} ${sucessos === 1 ? 'retorno ignorado' : 'retornos ignorados'} com sucesso. ${erros} ${erros === 1 ? 'erro' : 'erros'}.`,
                        'warning'
                    );
                }

                this.itensSelecionados = [];
                await this.carregarDados();
            } catch (error) {
                console.error('Erro ao processar lote:', error);
                this.mostrarMensagem(
                    'Erro ao processar retornos em lote',
                    'error'
                );
            } finally {
                this.loadingIgnorarLote = false;
                this.dialogConfirmacaoLote = false;
            }
        }
    }
};
</script>
